import React from 'react';
import styled from 'styled-components';
import { formatMoney, toLocalDateFormat } from '../../utils/functions';

// Ya no necesita ModalHeader porque el padre lo maneja

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 2px dashed #dee2e6;
`;

const SummaryContainer = styled.div`
  background: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e9ecef;
  
  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    
    &:last-child {
      margin-bottom: 0;
      padding-top: 0.5rem;
      border-top: 2px solid #dee2e6;
      font-weight: 700;
      font-size: 1.1rem;
      color: #212529;
    }
    
    &.no-children {
      margin-bottom: 0;
      padding-top: 0;
      border-top: none;
      font-weight: 700;
      font-size: 1.1rem;
      color: #212529;
    }
    
    .label {
      color: #6c757d;
      font-size: 0.9rem;
    }
    
    .amount {
      font-weight: 600;
      color: #495057;
      font-size: 1rem;
    }
  }
`;

const PaymentsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 0.25rem;
  
  /* Scrollbar personalizado */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const PaymentCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem; // Más compacto
  margin-bottom: 0.5rem; // Menos espacio
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    border-color: #86b7fe;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .payment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .date {
      font-weight: 600;
      color: #495057;
      font-size: 0.9rem;
    }
    
    .amount {
      font-weight: 700;
      color: #28a745;
      font-size: 1rem;
      text-align: right;
      
      &.rejected {
        color: #dc3545;
        text-decoration: line-through;
      }
    }
  }
  
  .payment-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .method {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #6c757d;
      font-size: 0.85rem;
      flex-shrink: 0;
    }
    
    .status {
      font-size: 0.7rem;
      padding: 0.15rem 0.4rem;
      border-radius: 0.25rem;
      font-weight: 600;
      white-space: nowrap;
      flex-shrink: 0;
      
      &.aplicado {
        background-color: #d4edda;
        color: #155724;
      }
      
      &.pendiente {
        background-color: #fff3cd;
        color: #856404;
      }
      
      &.anulado {
        background-color: #f8d7da;
        color: #721c24;
      }
    }
  }
  
  /* Responsive para móviles */
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem; // Un poco más de padding horizontal
    margin-bottom: 0.5rem;
    
    .payment-header {
      /* Primera línea: fecha y monto */
      margin-bottom: 0.4rem; // Espacio entre líneas
      
      .date {
        font-size: 0.95rem; // Un poco más grande en móvil
      }
      
      .amount {
        font-size: 1.1rem; // Más grande en móvil para destacarlo
      }
    }
    
    .payment-details {
      /* Segunda línea: método y estado */
      align-items: center;
      justify-content: space-between;
      
      .method {
        font-size: 0.9rem;
        gap: 0.4rem;
      }
      
      .status {
        font-size: 0.65rem; // Un poco más pequeño
        padding: 0.2rem 0.3rem;
      }
    }
  }
`;

interface PaymentsHistoryModalProps {
  orderId: number | null;
  payments: any[] | null | undefined;
  onClose: () => void;
}

const getPaymentIcon = (method: string) => {
  switch(method?.toUpperCase()) {
    case 'EFECTIVO': return '💰';
    case 'TRANSFERENCIA': return '🏦';
    case 'YAPE': return '💙';
    case 'PLIN': return '💵';
    case 'TARJETA': return '💳';
    default: return '💸';
  }
};

const getStatusClass = (status: string) => {
  switch(status?.toUpperCase()) {
    case 'REGISTRADO': return 'aplicado';
    case 'PENDIENTE': return 'pendiente';
    case 'RECHAZADO': return 'anulado';
    default: return '';
  }
};

export const PaymentsHistoryModal: React.FC<PaymentsHistoryModalProps> = ({
  orderId,
  payments,
  onClose
}) => {
  // Ya no necesitamos getDayTime porque no mostramos hora
  
  // Calcular totales
  const calculateTotals = () => {
    if (!payments || payments.length === 0) {
      return { registered: 0, pending: 0, total: 0 };
    }
    
    const registered = payments
      .filter(p => p.status?.toUpperCase() === 'REGISTRADO')
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    
    const pending = payments
      .filter(p => p.status?.toUpperCase() === 'PENDIENTE')
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    
    return { registered, pending, total: registered + pending };
  };

  const { registered, pending, total } = calculateTotals();

  return (
    <>
      {!payments ? (
        <LoadingMessage>Cargando...</LoadingMessage>
      ) : payments.length === 0 ? (
        <EmptyMessage>No hay cobros registrados aún.</EmptyMessage>
      ) : (
        <>
          {/* Resumen de totales */}
          <SummaryContainer>
            {pending > 0 ? (
              <>
                <div className="summary-row">
                  <span className="label">Total Registrado:</span>
                  <span className="amount">{formatMoney(registered)}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Total Pendiente:</span>
                  <span className="amount">{formatMoney(pending)}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Total Cobrado:</span>
                  <span className="amount">{formatMoney(total)}</span>
                </div>
              </>
            ) : (
              <div className="summary-row no-children">
                <span className="label">Total Cobrado:</span>
                <span className="amount">{formatMoney(registered)}</span>
              </div>
            )}
          </SummaryContainer>
          
          <PaymentsList>
            {payments.map((payment, index) => (
            <PaymentCard key={index}>
              <div className="payment-header">
                <div className="date">{toLocalDateFormat(payment.date)}</div>
                <div className={`amount ${payment.status?.toUpperCase() === 'RECHAZADO' ? 'rejected' : ''}`}>
                  {formatMoney(Number(payment.amount))}
                </div>
              </div>
              
              <div className="payment-details">
                <div className="method">
                  <span className="icon">{getPaymentIcon(payment.method)}</span>
                  <span>{payment.method?.toUpperCase() || 'SIN MÉTODO'}</span>
                </div>
                <div className={`status ${getStatusClass(payment.status)}`}>
                  {payment.status?.toUpperCase() || 'APLICADO'}
                </div>
              </div>
              
              {payment.notes && (
                <div className="notes" style={{ marginTop: '0.5rem', color: '#6c757d', fontSize: '0.85rem' }}>
                  📝 {payment.notes}
                </div>
              )}
            </PaymentCard>
            ))}
          </PaymentsList>
        </>
      )}
    </>
  );
};